import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

/* Previews (~1600px) for the device frame */
import MySaf from '../../images/collage/opt/mysaf_app.jpg';
import CheckSmart from '../../images/collage/opt/checksmart_app.jpg';
import SarnDashboard from '../../images/collage/opt/sarn_dashboard.jpg';
import FoodApp from '../../images/collage/opt/food_app.jpg';
import StudyApp from '../../images/collage/opt/study_app.jpg';
import QuizApp from '../../images/collage/opt/quiz_app.jpg';
import TekeTaxi from '../../images/collage/opt/teketaxi_app.jpg';
import OnboardingShot from '../../images/collage/opt/onboaring.jpg';
import Fuse from '../../images/collage/opt/fuse_image.jpg';
import Cards from '../../images/collage/opt/Dribbble_cards.jpg';
import Cashflow from '../../images/collage/opt/Dribbble_cashflow.jpg';
import Farm from '../../images/collage/opt/Dribbble_farm.jpg';
import FileManager from '../../images/collage/opt/Dribbble_file3.jpg';
import Concept from '../../images/collage/opt/Dribbble_image3.jpg';
import Insight from '../../images/collage/opt/Dribbble_insight.jpg';
import Surgeon from '../../images/collage/opt/Dribbble_sergeon.jpg';
import GetApp from '../../images/collage/opt/dribble_get.jpg';
import Evira from '../../images/collage/opt/evira_thumbnails.jpg';
import LandingPage from '../../images/collage/opt/landing_page.jpg';

/* Thumbnails (~240px) for the dock icons */
import MySafT from '../../images/collage/opt/mysaf_app_thumb.jpg';
import CheckSmartT from '../../images/collage/opt/checksmart_app_thumb.jpg';
import SarnDashboardT from '../../images/collage/opt/sarn_dashboard_thumb.jpg';
import FoodAppT from '../../images/collage/opt/food_app_thumb.jpg';
import StudyAppT from '../../images/collage/opt/study_app_thumb.jpg';
import QuizAppT from '../../images/collage/opt/quiz_app_thumb.jpg';
import TekeTaxiT from '../../images/collage/opt/teketaxi_app_thumb.jpg';
import OnboardingT from '../../images/collage/opt/onboaring_thumb.jpg';
import FuseT from '../../images/collage/opt/fuse_image_thumb.jpg';
import CardsT from '../../images/collage/opt/Dribbble_cards_thumb.jpg';
import CashflowT from '../../images/collage/opt/Dribbble_cashflow_thumb.jpg';
import FarmT from '../../images/collage/opt/Dribbble_farm_thumb.jpg';
import FileManagerT from '../../images/collage/opt/Dribbble_file3_thumb.jpg';
import ConceptT from '../../images/collage/opt/Dribbble_image3_thumb.jpg';
import InsightT from '../../images/collage/opt/Dribbble_insight_thumb.jpg';
import SurgeonT from '../../images/collage/opt/Dribbble_sergeon_thumb.jpg';
import GetAppT from '../../images/collage/opt/dribble_get_thumb.jpg';
import EviraT from '../../images/collage/opt/evira_thumbnails_thumb.jpg';
import LandingPageT from '../../images/collage/opt/landing_page_thumb.jpg';

const APPS = [
    {
        title: 'My Safaricom App',
        icon: MySafT,
        shot: MySaf,
        about: 'A unified self-service mobile experience for Safaricom subscribers — managing airtime, data bundles, M-PESA, and support from a single home screen.',
        story: 'The challenge was making one app feel personal to 50M+ users with wildly different needs. I redesigned the architecture around usage patterns — heavy M-PESA users see their flows first, data users see bundles, business users see invoices. One app, many entry points.',
    },
    {
        title: 'CheckSmart',
        icon: CheckSmartT,
        shot: CheckSmart,
        about: 'A smart task and checklist app that learns from your habits and surfaces the right tasks at the right time.',
        story: 'I got frustrated with task apps that just list things without context. CheckSmart was my attempt at building a to-do tool that respects your energy levels throughout the day — light tasks in the morning, deep work when you hit your stride.',
    },
    {
        title: 'Sarn Dashboard',
        icon: SarnDashboardT,
        shot: SarnDashboard,
        about: 'An analytics and reporting dashboard for enterprise teams, designed to surface insights without overwhelming users with raw data.',
        story: 'Most dashboards are built for the person who requested the data, not the person who has to act on it. Sarn was designed the other way round — starting from decisions, not metrics. What does a team lead need to decide at 9am on a Monday?',
    },
    {
        title: 'Food Delivery',
        icon: FoodAppT,
        shot: FoodApp,
        about: 'A food ordering experience focused on speed and delight — browse, customise, and track your order in under 60 seconds.',
        story: 'Built during a hackathon for local restaurants in Nairobi. The constraint: most users were on 3G and ordering on low-end Androids. Every screen had to load fast and every tap had to be forgiving. We cut the checkout flow from 7 steps to 3.',
    },
    {
        title: 'Study App',
        icon: StudyAppT,
        shot: StudyApp,
        about: 'A focused learning companion for students — spaced repetition, progress tracking, and distraction-free study sessions built in.',
        story: 'I built this while mentoring university students who were struggling with digital distraction. The design philosophy: remove everything that isn\'t learning. No social feed, no streaks, no gamification theatre — just you and the material.',
    },
    {
        title: 'Quiz App',
        icon: QuizAppT,
        shot: QuizApp,
        about: 'A trivia and quiz platform with live multiplayer rounds, custom decks, and real-time leaderboards.',
        story: 'What started as a pub quiz side project turned into a fully fledged multiplayer platform. The hardest design problem was making the waiting room feel fun rather than awkward — turns out animated countdown timers and cheeky loading copy go a long way.',
    },
    {
        title: 'Teke Taxi',
        icon: TekeTaxiT,
        shot: TekeTaxi,
        about: 'A ride-hailing app built for the Nairobi market — supporting cash, M-PESA, and boda boda (motorcycle taxi) rides alongside standard cars.',
        story: 'Nairobi\'s transport reality is messy: matatus, bodas, and tuk-tuks sharing the road with Ubers. Teke was designed to embrace that chaos rather than ignore it — giving riders a single screen to choose the right vehicle for their route and budget.',
    },
    {
        title: 'Onboarding Flow',
        icon: OnboardingT,
        shot: OnboardingShot,
        about: 'A modular onboarding system designed to maximise activation — adapting the first-run experience to each user\'s stated goals.',
        story: 'This concept came out of a drop-off analysis that showed 60% of new users churned at the same screen: "choose your use case." The redesign replaces that binary choice with a short conversation — three questions, personalised outcome, users feel seen before they even open the app.',
    },
    {
        title: 'Fuse',
        icon: FuseT,
        shot: Fuse,
        about: 'A community platform for creative professionals — mood boards, project updates, and async collaboration in one feed.',
        story: 'I built Fuse because I wanted a space that felt like Dribbble\'s energy but with real collaboration tools. The feed is deliberately slow — no infinite scroll, no algorithmic sorting. You see what\'s new from the people you follow. That\'s it.',
    },
    {
        title: 'Cards UI',
        icon: CardsT,
        shot: Cards,
        about: 'A design exploration of card-based UI patterns — exploring hierarchy, density, and interaction states across mobile and web.',
        story: 'An exercise I run with every junior designer on my team: design the same information as a card, three different ways. This was my own attempt — pushing how much can live in a card before it stops being a card and starts being a page.',
    },
    {
        title: 'Cashflow',
        icon: CashflowT,
        shot: Cashflow,
        about: 'A personal finance app focused on cash flow awareness — helping users understand money movement, not just balances.',
        story: 'Most finance apps show you a number (your balance) when what you really need to know is a story (where your money goes and when it runs out). Cashflow is built around that narrative — weekly spending arcs, recurring pattern detection, and projections.',
    },
    {
        title: 'Farm App',
        icon: FarmT,
        shot: Farm,
        about: 'An agritech app connecting smallholder farmers to market prices, weather alerts, and input suppliers across East Africa.',
        story: 'Designed with farmers in mind, not agronomists. The language is plain, the icons are literal, and the app works offline — because most farms in Kenya don\'t have reliable connectivity. Every feature had to survive a field test in Nakuru before it shipped.',
    },
    {
        title: 'File Manager',
        icon: FileManagerT,
        shot: FileManager,
        about: 'A clean, keyboard-first file management experience for power users who live in their filesystem.',
        story: 'I find most file managers either too simple or impossibly cluttered. This concept strips back the interface to the essentials — quick access, bulk actions, and smart search — wrapped in a UI that gets out of your way.',
    },
    {
        title: 'Concept UI',
        icon: ConceptT,
        shot: Concept,
        about: 'An exploratory UI concept pushing the boundaries of depth, motion, and spatial design on mobile.',
        story: 'I give myself one free project a quarter where the only brief is: make something beautiful that you\'d actually want to use. This was that project. No client, no deadline, no brief. Just craft.',
    },
    {
        title: 'Insights',
        icon: InsightT,
        shot: Insight,
        about: 'A lightweight analytics tool for indie makers — tracking the metrics that actually matter without the enterprise overhead.',
        story: 'Built out of frustration with tools that require a data team to interpret them. Insights is designed for a solo founder checking in at 7am: one screen, three numbers, one action item. Done.',
    },
    {
        title: 'Surgeon',
        icon: SurgeonT,
        shot: Surgeon,
        about: 'A clinical workflow application designed to reduce cognitive load for surgical teams before, during, and after procedures.',
        story: 'Working on this project taught me more about design than almost anything else. When the cost of a bad interaction is a human life, every decision is loaded. I spent two weeks shadowing surgical nurses before touching Figma.',
    },
    {
        title: 'Get',
        icon: GetAppT,
        shot: GetApp,
        about: 'An on-demand delivery platform for everyday essentials — groceries, pharmacy, and household items delivered in under an hour.',
        story: 'Get started as a pandemic project when Nairobi went into lockdown and hyperlocal delivery became critical. The design challenge was building trust — helping users feel confident ordering from a store they\'d never visited through an app they\'d just downloaded.',
    },
    {
        title: 'Evira',
        icon: EviraT,
        shot: Evira,
        about: 'A wellness app for women — cycle tracking, mood journalling, and personalised health insights in a calming, private space.',
        story: 'Most health apps for women were designed by teams that didn\'t include women. Evira started with a different process: six months of qualitative research, a female-led design sprint, and a content strategy built around nuance rather than pink gradients.',
    },
    {
        title: 'Landing Page',
        icon: LandingPageT,
        shot: LandingPage,
        about: 'A high-conversion landing page design system — modular sections, proven copy patterns, and built-in A/B test scaffolding.',
        story: 'I\'ve built enough landing pages to know the patterns that convert and the ones that just look good in a portfolio. This system packages those learnings — a kit you can assemble in an afternoon that actually moves the needle.',
    },
];

/* ── Styled components ─────────────────────────────────────── */

const Card = styled.div`
    background: ${({ theme }) => theme.altWhite};
    border-radius: var(--radius-xl);
    padding: 56px;
    overflow: hidden;

    @media (max-width: 768px) {
        padding: 28px 20px;
        border-radius: 22px;
    }
`;

const CardHeading = styled.h3`
    font-family: 'Clash Display', 'Inter', sans-serif;
    font-size: clamp(22px, 3vw, 30px);
    font-weight: 600;
    letter-spacing: -0.6px;
    color: ${({ theme }) => theme.textMain};
    margin: 0 0 28px;
`;

const DeviceFrame = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 10;
    border-radius: 22px;

    @media (max-width: 768px) {
        aspect-ratio: 4 / 3;
        border-radius: 16px;
    }
`;

const Screen = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 14px;
    overflow: hidden;
    background: #e9e9ea;
    cursor: pointer;
    position: relative;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top center;
        display: block;
        transition: transform 0.3s ease;
    }

    &:hover img {
        transform: scale(1.02);
    }

    /* "Tap to expand" hint badge */
    &::after {
        content: '⤢  View';
        position: absolute;
        bottom: 14px;
        right: 14px;
        background: rgba(0, 0, 0, 0.56);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        color: #fff;
        font-family: var(--font-body);
        font-size: 13px;
        font-weight: 500;
        letter-spacing: -0.01em;
        padding: 6px 12px;
        border-radius: 999px;
        pointer-events: none;
        opacity: 0;
        transform: translateY(4px);
        transition: opacity 0.2s ease, transform 0.2s ease;
    }

    &:hover::after {
        opacity: 1;
        transform: translateY(0);
    }
`;

const Dots = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;
`;

const Dot = styled.button`
    width: ${({ $active }) => ($active ? '22px' : '7px')};
    height: 7px;
    border: none;
    padding: 0;
    border-radius: 999px;
    cursor: pointer;
    transition: width 0.25s ease, opacity 0.25s ease;
    background: ${({ theme }) => theme.textMain};
    opacity: ${({ $active }) => ($active ? 0.85 : 0.25)};
`;

const Dock = styled.div`
    display: flex;
    flex-wrap: nowrap;
    gap: 14px;
    margin-top: 36px;
    overflow-x: auto;
    padding: 8px 0;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar { display: none; }

    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 40px, #000 calc(100% - 40px), transparent 100%);
    mask-image: linear-gradient(to right, transparent 0, #000 40px, #000 calc(100% - 40px), transparent 100%);

    @media (max-width: 768px) {
        gap: 12px;
        -webkit-mask-image: linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
        mask-image: linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
    }
`;

const IconWrap = styled.div`
    position: relative;
    flex-shrink: 0;

    /* Tooltip */
    &::before {
        content: attr(data-label);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%) translateY(4px);
        background: rgba(15, 15, 15, 0.88);
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
        color: #fff;
        font-family: var(--font-body);
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        padding: 5px 10px;
        border-radius: 8px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s ease, transform 0.15s ease;
        z-index: 20;
    }

    &:hover::before {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
`;

const IconButton = styled.button`
    width: 64px;
    height: 64px;
    padding: 0;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    background: ${({ theme }) => theme.altWhite};
    border: 2px solid ${({ theme, $active }) => ($active ? theme.colorPrimary : theme.outline)};
    box-shadow: ${({ $active }) => ($active ? '0 6px 18px rgba(0,0,0,0.16)' : '0 2px 8px rgba(0,0,0,0.06)')};
    transform: ${({ $active }) => ($active ? 'translateY(-3px)' : 'none')};
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
    display: block;

    &:hover { transform: translateY(-3px); }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    @media (max-width: 768px) {
        width: 52px;
        height: 52px;
        border-radius: 13px;
    }
`;

/* ── Modal ─────────────────────────────────────────────────── */

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.52);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
        from { opacity: 0; }
        to   { opacity: 1; }
    }
`;

const ModalCard = styled.div`
    background: #fff;
    border-radius: 28px;
    width: 100%;
    max-width: 680px;
    max-height: 90vh;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar { display: none; }
    animation: slideUp 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;

    @keyframes slideUp {
        from { opacity: 0; transform: translateY(24px) scale(0.97); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
    }
`;

const ModalHeader = styled.div`
    border-radius: 24px 24px 0 0;
    overflow: hidden;
    position: relative;
    line-height: 0;

    img {
        width: 100%;
        max-height: 380px;
        object-fit: cover;
        object-position: top center;
        display: block;
    }
`;


const CloseBtn = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.18);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    line-height: 1;
    transition: background 0.15s ease;
    z-index: 10;

    &:hover { background: rgba(0, 0, 0, 0.32); }
`;

const ModalBody = styled.div`
    padding: 28px 32px 36px;

    @media (max-width: 520px) {
        padding: 20px 20px 28px;
    }
`;

const ModalAppRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
`;

const ModalIcon = styled.img`
    width: 68px;
    height: 68px;
    border-radius: 18px;
    object-fit: cover;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const ModalAppTitle = styled.h3`
    font-size: clamp(20px, 3vw, 26px);
    font-weight: 600;
    margin: 0 0 4px;
    letter-spacing: -0.03em;
    color: #0a0a0a;
    flex: 1;
`;

const AppStoreBtn = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #1a7f64;
    color: #fff;
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.01em;
    padding: 10px 18px;
    border-radius: 999px;
    text-decoration: none;
    flex-shrink: 0;
    transition: opacity 0.15s ease;

    &:hover { opacity: 0.88; }

    svg { flex-shrink: 0; }
`;

const ModalDivider = styled.hr`
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    margin: 0 0 20px;
`;

const ModalLabel = styled.p`
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #1a7f64;
    margin: 0 0 8px;
`;

const ModalText = styled.p`
    font-family: var(--font-body);
    font-size: 16px;
    line-height: 1.65;
    color: #111;
    margin: 0 0 24px;
`;

/* ── Component ─────────────────────────────────────────────── */

const AppShowcase = ({ header }) => {
    const [active, setActive] = useState(0);
    const [modalApp, setModalApp] = useState(null);
    const current = APPS[active];

    useEffect(() => {
        if (!modalApp) return;
        const onKey = (e) => { if (e.key === 'Escape') setModalApp(null); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [modalApp]);

    return (
        <>
            <Card>
                {header ?? <CardHeading>I build apps. A lot of apps.</CardHeading>}

                <DeviceFrame>
                    <Screen onClick={() => setModalApp(current)}>
                        <img src={current.shot} alt={current.title} />
                    </Screen>
                </DeviceFrame>

                <Dots>
                    {APPS.map((app, i) => (
                        <Dot
                            key={app.title}
                            $active={i === active}
                            onClick={() => setActive(i)}
                            aria-label={`Show ${app.title}`}
                        />
                    ))}
                </Dots>

                <Dock>
                    {APPS.map((app, i) => (
                        <IconWrap key={app.title} data-label={app.title}>
                            <IconButton
                                $active={i === active}
                                onClick={() => setActive(i)}
                                aria-label={app.title}
                            >
                                <img src={app.icon} alt={app.title} loading="lazy" decoding="async" />
                            </IconButton>
                        </IconWrap>
                    ))}
                </Dock>
            </Card>

            {modalApp && (
                <Overlay onClick={() => setModalApp(null)}>
                    <ModalCard onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <CloseBtn onClick={() => setModalApp(null)} aria-label="Close">✕</CloseBtn>
                            <img src={modalApp.shot} alt={modalApp.title} />
                        </ModalHeader>

                        <ModalBody>
                            <ModalAppRow>
                                <ModalIcon src={modalApp.icon} alt={modalApp.title} />
                                <div style={{ flex: 1 }}>
                                    <ModalAppTitle>{modalApp.title}</ModalAppTitle>
                                    <p style={{ margin: 0, fontSize: 13, color: '#888', fontFamily: 'var(--font-body)' }}>Daily mood-matched motivation</p>
                                </div>
                            </ModalAppRow>

                            <ModalDivider />

                            <ModalLabel>About</ModalLabel>
                            <ModalText>{modalApp.about}</ModalText>

                            <ModalLabel>The Story</ModalLabel>
                            <ModalText style={{ marginBottom: 0 }}>{modalApp.story}</ModalText>
                        </ModalBody>
                    </ModalCard>
                </Overlay>
            )}
        </>
    );
};

export { AppShowcase };
